import React from 'react';
import { Autocomplete, Box, Chip, Stack, TextField, Typography } from '@mui/material';

const list = (v) => (Array.isArray(v) ? v : []);

// The starting point for a script's parameters: the mandatory ones empty, of the right kind. DryRun is left out: the API adds it
// (true to check, false to change).
const EMPTY_BY_TYPE = { Array: [], Boolean: false, Int32: 0, Int64: 0, Double: 0 };
export const parameterTemplate = (script) => {
  const out = {};
  list(script?.variables).filter((v) => v.mandatory && v.name !== 'DryRun').forEach((v) => {
    out[v.name] = Object.prototype.hasOwnProperty.call(EMPTY_BY_TYPE, v.type) ? EMPTY_BY_TYPE[v.type] : '';
  });
  return Object.keys(out).length ? JSON.stringify(out) : '';
};

// A script chosen from the ones VES has published (typing a path still works, for one that is not published yet). `script` is the
// path; onChange gets the path and, when it is a published script, that script's entry.
function ScriptPicker({ label, value, scripts, onChange }) {
  const options = list(scripts);
  const current = options.find((s) => s.functionPath === value);
  return (
    <Stack spacing={1}>
      <Autocomplete
        freeSolo size="small" options={options} value={current || null} inputValue={value || ''}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.functionPath)}
        groupBy={(o) => o.functionPath.split('/').slice(0, -1).join('/')}
        isOptionEqualToValue={(o, v) => o.functionPath === (v.functionPath || v)}
        filterOptions={(all, state) => all.filter((o) => `${o.functionPath} ${o.description}`.toLowerCase().includes(state.inputValue.trim().toLowerCase()))}
        onInputChange={(e, text, reason) => { if (reason !== 'reset') onChange(text, options.find((s) => s.functionPath === text)); }}
        onChange={(e, option) => { if (option && typeof option !== 'string') onChange(option.functionPath, option); }}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.functionPath}>
            <Box>
              <Typography variant="body2">{o.name}</Typography>
              <Typography variant="caption" color="text.secondary">{o.functionPath}{o.description ? ` - ${o.description.split('\n')[0]}` : ''}</Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label={label} helperText={options.length ? 'Choose a published script, or type a path.' : 'The published scripts could not be listed: type the path.'} />}
      />
      {current && list(current.variables).length > 0 && (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="caption" color="text.secondary">Takes:</Typography>
          {current.variables.map((v) => (
            <Chip key={v.name} size="small" variant="outlined" color={v.mandatory ? 'primary' : 'default'} label={`${v.name} (${v.type || 'any'}${v.mandatory ? ', required' : ''})`} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default ScriptPicker;
